import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'

import { useModal } from '@/contexts/modal'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { useTableSettings } from '@/hooks/useTableSettings'
import useAuthStore from '@/stores/useAuthStore'

import { getBusinessesList, getAllBusinessesList } from '../services'
import { Business } from '../types/response'
import { getBusinessListColumns } from '../components/List/columns'
import {
    getHrmActions,
    getMemberActions,
    getSelectionActions,
    getFilterItems,
    getFilterToggles,
    getPreviewColumns,
} from '../utils/business-list-config'
import {
    SingleApprovalProps,
    BulkApprovalProps,
} from '@/modals/BusinessModals/BusinessApprovalModal'

interface BusinessFilters {
    business_type: string
    business_status: string
    business_startdate: string[] // [from, to] range sent as single key to API
    business_enddate: string[] // form-only field for daterangepicker UI
    department_id: number
    position_id: number
    employee_ids: number[]
}

export const useBusinessList = (pageType: 'member' | 'hrm') => {
    const navigate = useNavigate()
    const { setModal } = useModal()
    const { t } = useTranslation('business')
    const { employee } = useAuthStore()

    // Selection state
    const [selectedKeys, setSelectedKeys] = useState<string[]>([])

    // Table settings
    const { getHiddenOptions } = useTableSettings('business-list')
    const hiddenEmployeeOptions = getHiddenOptions('employeeOptions')
    const employeeActiveOnly = !hiddenEmployeeOptions.includes('activeOnly')

    // URL params & form
    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        setFilters,
        searchInputProps,
    } = useUrlSearchParams<BusinessFilters>({
        defaults: { sort_by: 'business_id', sort_order: 'desc' },
        filterDefaults: {
            business_type: '',
            business_status: '',
            business_startdate: [],
            business_enddate: [],
            department_id: 0,
            position_id: 0,
            employee_ids: [],
        },
    })

    const { control, setValue } = useForm<BusinessFilters>({
        defaultValues: filters,
    })

    // Data fetching
    const apiCall =
        pageType === 'hrm' ? getAllBusinessesList : getBusinessesList
    const { data, refetch, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.BUSINESS, pageType, queryParams],
        queryFn: () => apiCall(queryParams),
        placeholderData: true,
    })

    // Modal handlers
    const handleLeaderConfirm = useCallback(
        (row: Business) => {
            setModal<SingleApprovalProps>(
                'BusinessModals/BusinessApprovalModal/index',
                {
                    mode: 'single',
                    type: 'leader',
                    businessID: row.business_id,
                    employeeID: row.employee_id,
                    feedbackInitial: row.business_leader_feedback ?? '',
                    businessLeaderStatus: row.business_leader_status ?? '',
                    businessStatus: row.business_status ?? '',
                    businessBodStatus: row.business_bod_status ?? '',
                    refetch,
                }
            )
        },
        [setModal, refetch]
    )

    const handleBodConfirm = useCallback(
        (row: Business) => {
            setModal<SingleApprovalProps>(
                'BusinessModals/BusinessApprovalModal/index',
                {
                    mode: 'single',
                    type: 'bod',
                    businessID: row.business_id,
                    feedbackInitial: row.business_bod_feedback ?? '',
                    businessStatus: row.business_status ?? '',
                    businessBodStatus: row.business_bod_status ?? '',
                    refetch,
                }
            )
        },
        [setModal, refetch]
    )

    // Cancel handler (member)
    const handleCancel = useCallback(
        (row: Business) => {
            setModal('BusinessModals/Cancelled/index', {
                open: true,
                selectedBusinessId: row.business_id,
                onClose: () => {
                    setModal('BusinessModals/Cancelled/index', {
                        open: false,
                        selectedBusinessId: null,
                        onClose: () => {},
                        refetchData: () => {},
                    })
                },
                refetchData: () => refetch(),
            })
        },
        [setModal, refetch]
    )

    // Bulk approve handler
    const handleBulkApprove = useCallback(
        (selectedRows: Business[]) => {
            const ids = selectedRows.map((row) => row.business_id)
            setModal<BulkApprovalProps>(
                'BusinessModals/BusinessApprovalModal/index',
                {
                    mode: 'bulk',
                    ids,
                    rows: selectedRows,
                    refetch: () => {
                        refetch()
                        setSelectedKeys([])
                    },
                }
            )
        },
        [setModal, refetch]
    )

    // Memoized configs
    const columns = useMemo(() => getBusinessListColumns(t), [t])

    const actions = useMemo(
        () =>
            pageType === 'hrm'
                ? getHrmActions({
                      t,
                      onLeaderConfirm: handleLeaderConfirm,
                      onBodConfirm: handleBodConfirm,
                      currentEmployeeId: employee?.employee_id,
                  })
                : getMemberActions({ t, onCancel: handleCancel }),
        [
            t,
            pageType,
            handleLeaderConfirm,
            handleBodConfirm,
            employee?.employee_id,
            handleCancel,
        ]
    )

    const selectionActions = useMemo(
        () => getSelectionActions({ t, onBulkApprove: handleBulkApprove }),
        [t, handleBulkApprove]
    )

    const filterItems = useMemo(
        () =>
            getFilterItems({
                t,
                pageType,
                filters,
                employeeActiveOnly,
                setValue,
                setFilters,
            }),
        [t, pageType, filters, employeeActiveOnly, setValue, setFilters]
    )

    const filterToggles = useMemo(
        () => getFilterToggles(t, pageType),
        [t, pageType]
    )
    const previewColumns = useMemo(() => getPreviewColumns(t), [t])

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        // Data
        businesses: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),

        // Form & params
        control,
        setFilter,
        params,
        queryParams,
        searchInputProps,

        // Selection
        selectedKeys,
        setSelectedKeys,

        // Configs
        columns,
        actions,
        selectionActions,
        filterItems,
        filterToggles,
        previewColumns,
        employeeActiveOnly,

        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        navigate,
        t,
    }
}
